import { Pressable, Text, View, useWindowDimensions } from 'react-native'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Animated, {
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated'
import { colors } from '@chillout/core'
import { Button } from '@chillout/ui'
import { QRScanner } from '../shared/QRScanner'

const HORIZONTAL_PADDING = 48
const VIEWFINDER_MAX_SIZE = 520

interface ScannerPanelProps {
  onEnterManually: () => void
  onScanned: (data: string) => void
  scannerActive: boolean
  isIdle: boolean
  onResume: () => void
}

export function ScannerPanel({ onEnterManually, onScanned, scannerActive, isIdle, onResume }: ScannerPanelProps) {
  const { t } = useTranslation()
  const { width, height } = useWindowDimensions()
  const viewfinderSize = Math.min(width - HORIZONTAL_PADDING, height * 0.5, VIEWFINDER_MAX_SIZE)
  const overlayOpacity = useSharedValue(0)

  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (isIdle) {
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current)
      overlayOpacity.value = 1
    } else {
      fadeTimerRef.current = setTimeout(() => {
        overlayOpacity.value = withTiming(0, { duration: 500, easing: Easing.out(Easing.ease) })
      }, 300)
    }
    return () => {
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current)
    }
  }, [isIdle, overlayOpacity])

  const overlayStyle = useAnimatedStyle(() => ({
    opacity: overlayOpacity.value,
    pointerEvents: overlayOpacity.value > 0 ? 'auto' : 'none',
  }))

  return (
    <View className="flex-1 px-6">
      <View className="flex-1 items-center justify-center">
        <Text
          className="text-3xl font-bold text-primary"
          accessibilityLabel={t('cashierHome.scannerTitleAccessibilityLabel')}
        >
          {t('cashierHome.scannerTitle')}
        </Text>
        <Text className="mt-2 text-base lg:text-lg text-secondary">
          {t('cashierHome.scannerSubtitle')}
        </Text>
        <View className="my-10" style={{ width: viewfinderSize, height: viewfinderSize }}>
          <QRScanner
            active={scannerActive && !isIdle}
            onScanned={onScanned}
            strings={{
              permissionDescription: t('cashierHome.cameraPermissionDescription'),
              permissionDeniedDescription: t('cashierHome.cameraPermissionDeniedDescription'),
              permissionButton: t('cashierHome.cameraPermissionButton'),
              settingsButton: t('cashierHome.cameraSettingsButton'),
            }}
          />
          <Animated.View
            style={[overlayStyle, { position: 'absolute', inset: 0 }]}
          >
            <Pressable
              onPress={onResume}
              accessibilityRole="button"
              accessibilityLabel={t('cashierHome.idleAccessibilityLabel')}
              className="flex-1 items-center justify-center rounded-2xl"
              style={{ backgroundColor: colors.canvas.dark + 'CC' }}
            >
              <Text className="text-base lg:text-lg font-semibold text-white">
                {t('cashierHome.tapToResume')}
              </Text>
            </Pressable>
          </Animated.View>
        </View>
        <View className="items-center">
          <Text className="text-base lg:text-lg text-secondary">
            {t('cashierHome.cantScanHint')}
          </Text>
          <Button
            label={t('cashierHome.enterManually')}
            onPress={onEnterManually}
            variant="ghost"
            fullWidth={false}
            accessibilityLabel={t('cashierHome.enterManuallyAccessibilityLabel')}
          />
        </View>
      </View>
    </View>
  )
}